import { useMutation } from '@redwoodjs/web';
import { enqueueSnackbar } from 'notistack';
import {
  CreateLedgerAccountDetailMutation,
  CreateLedgerAccountDetailMutationVariables,
  DeleteLedgerAccountDetailMutation,
  DeleteLedgerAccountDetailMutationVariables,
  DisableLedgerAccountDetailMutation,
  DisableLedgerAccountDetailMutationVariables,
  UpdateLedgerAccountDetailMutation,
  UpdateLedgerAccountDetailMutationVariables,
} from 'types/graphql';

const CREATE_ACCOUNT = gql`
  mutation CreateLedgerAccountDetailMutation($input: CreateLedgerAccountDetailInput!) {
    createLedgerAccountDetail(input: $input) {
      id
      accountName
      accountNumber
      accountType
      parentId
      fragmentAccountPath
      isPhysical
      disabled
    }
  }
`;

const UPDATE_ACCOUNT = gql`
  mutation UpdateLedgerAccountDetailMutation($id: Int!, $input: UpdateLedgerAccountDetailInput!) {
    updateLedgerAccountDetail(id: $id, input: $input) {
      id
      accountName
      accountNumber
      accountType
      parentId
      fragmentAccountPath
      isPhysical
      disabled
    }
  }
`;

const DISABLE_ACCOUNT = gql`
  mutation DisableLedgerAccountDetailMutation($id: Int!, $disabled: Boolean!) {
    disableLedgerAccountDetail(id: $id, disabled: $disabled) {
      id
      disabled
    }
  }
`;

const DELETE_ACCOUNT = gql`
  mutation DeleteLedgerAccountDetailMutation($id: Int!) {
    deleteLedgerAccountDetail(id: $id) {
      id
    }
  }
`;

export function useCreateAccount() {
  const [createAccount, { loading, error }] = useMutation<
    CreateLedgerAccountDetailMutation,
    CreateLedgerAccountDetailMutationVariables
  >(CREATE_ACCOUNT, {
    onCompleted: () => {
      enqueueSnackbar('Account created successfully');
    },
    refetchQueries: ['GetLedgerAccounts'],
    awaitRefetchQueries: true,
  });

  return { createAccount, loading, error };
}

export function useUpdateAccount() {
  const [updateAccount, { loading, error }] = useMutation<
    UpdateLedgerAccountDetailMutation,
    UpdateLedgerAccountDetailMutationVariables
  >(UPDATE_ACCOUNT, { refetchQueries: ['GetLedgerAccounts'], awaitRefetchQueries: true });

  return { updateAccount, loading, error };
}

export function useDisableAccount() {
  const [disableAccount, { loading, error }] = useMutation<
    DisableLedgerAccountDetailMutation,
    DisableLedgerAccountDetailMutationVariables
  >(DISABLE_ACCOUNT, {
    onCompleted: (_data, context) => {
      const action = context?.variables?.disabled ? 'disabled' : 'enabled';
      enqueueSnackbar(`Account ${action} successfully`);
    },
    refetchQueries: ['GetLedgerAccounts'],
    awaitRefetchQueries: true,
  });

  return { disableAccount, loading, error };
}

export function useDeleteAccount() {
  const [deleteAccount, { loading, error }] = useMutation<
    DeleteLedgerAccountDetailMutation,
    DeleteLedgerAccountDetailMutationVariables
  >(DELETE_ACCOUNT, {
    onCompleted: () => {
      enqueueSnackbar('Account deleted successfully');
    },
    refetchQueries: ['GetLedgerAccounts'],
    awaitRefetchQueries: true,
  });

  return { deleteAccount, loading, error };
}
